import * as WebBrowser from 'expo-web-browser';
import React, { useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { Button, Divider, Heading, Layout, Link, Text } from '../../../components/core';
import { IconMessageCircle } from '../../../components/icons/IconMessageCircle';
import { IconVideo } from '../../../components/icons/IconVideo';
import { HubConnectNowData, useNavigateFromHubMobileToWebLazyQuery } from '../../../graphQL';
import { getRoute, useExternalNavigate } from '../../../routes';
import { getStylesheet } from '../../../styles';
import { visuallyHiddenFloatStyle } from '../../../utils/accessibility';
import {
  ConnectNowAvailabilityStatuses,
  ConnectNowContactMethods,
  ContactMethodDefinition,
} from '../connectNowTypes';
import {
  ContactMethodWithAvailability,
  useConnectNowContactMethods,
} from '../hooks/useConnectNowContactMethods';
import { isDuringHoliday } from '../utils/availability';
import { BrowserIncompatibilityWarning } from './BrowserIncompatibilityWarning';
import { ContactMethod } from './ContactMethod';
import { InterpretationSupportModal } from './InterpretationSupportModal';

type HubConnectNowConfig = {
  phone: string;
  sms: string;
  smsInstructions: string;
  smsNumber: string;
  sms247: boolean;
  webchat: string;
  webchat247: boolean;
};

const getContactOptions = (data: HubConnectNowData): HubConnectNowConfig => ({
  phone: data.crisisCallPhoneNumber,
  sms: data.crisisSMS ?? '', // Legacy field, will be deprecated in favor of crisisSMSInstructions and crisisSMSNumber
  smsInstructions: data.crisisSMSInstructions ?? '',
  smsNumber: data.crisisSMSPhone ?? '',
  sms247: data.crisisSMS247 ?? false,
  webchat: data.crisisWebChat ?? '',
  webchat247: data.crisisWebChat247 ?? false,
});

type ContactMethodsProps = {
  connectNowData: HubConnectNowData;
  isDuringServiceHours: boolean;
  interpretationServicesSMS?: string;
};

export const ContactMethods = ({
  connectNowData,
  isDuringServiceHours,
  interpretationServicesSMS,
}: ContactMethodsProps): JSX.Element => {
  const [showBrowserWarning, setShowBrowserWarning] = useState(false);
  const [showInterpretationModal, setShowInterpretationModal] = useState(false);
  const externalNavigate = useExternalNavigate();
  const [queryForHubWebLoginUrl] = useNavigateFromHubMobileToWebLazyQuery();
  const contactOptions = useMemo(() => getContactOptions(connectNowData), [connectNowData]);
  const usesInterpretationServices = interpretationServicesSMS !== undefined;

  const onVideoCallClick = async (): Promise<void> => {
    if (Platform.OS === 'web') {
      externalNavigate(getRoute('connectNowConfirmInfo', {}));
    } else if (Platform.OS === 'android') {
      setShowBrowserWarning(true);
    } else {
      await openBrowser();
    }
  };

  const openBrowser = async (): Promise<void> => {
    const { data } = await queryForHubWebLoginUrl({ variables: { source: 'connectNow' } });
    const hubWebLoginUrl = data?.navigateFromHubMobileToWeb;
    if (hubWebLoginUrl !== undefined) {
      void WebBrowser.openBrowserAsync(hubWebLoginUrl);
    }
  };

  const shouldShowInterpretationButton = (method: ContactMethodWithAvailability): boolean => {
    return (
      usesInterpretationServices &&
      method.type === 'Video' &&
      method.availabilityStatus === ConnectNowAvailabilityStatuses.Available
    );
  };

  const contactMethodDefinitions = useMemo<ContactMethodDefinition[]>(() => {
    const methods: ContactMethodDefinition[] = [];

    /**
     * Video
     * Available 12-12est
     * Follows holiday schedule
     * Optionally shows interpretation prompt based on
     * usesInterpretationServices flag
     */
    methods.push({
      type: ConnectNowContactMethods.Video,
      title: 'Video',
      is247: false,
      availableElement: (
        <Button.primaryMedium
          aria-label={
            Platform.OS === 'web' ? 'Connect (opens in new tab)' : 'Connect (opens in web browser)'
          }
          isLink
          leftIcon={<IconVideo aria-hidden />}
          onPress={onVideoCallClick}
          testID="button-connect-now-video-call"
        >
          Connect
        </Button.primaryMedium>
      ),
    });

    /**
     * Webchat
     * Available 12-12est or 24/7 based on webchat247
     * If not 24/7, follows holiday schedule
     */
    if (contactOptions.webchat !== '') {
      methods.push({
        type: ConnectNowContactMethods.WebChat,
        title: 'Web Chat',
        is247: contactOptions.webchat247,
        availableElement: (
          <Button.secondaryMedium
            aria-label="Chat (opens in new tab)"
            isLink
            testID="button-connect-now-chat"
            onPress={() => externalNavigate(contactOptions.webchat)}
            leftIcon={<IconMessageCircle aria-hidden />}
          >
            Chat
          </Button.secondaryMedium>
        ),
      });
    }
    /**
     * SMS
     * Available 12-12est or 24/7 based on sms247
     * If not 24/7, follows holiday schedule
     *
     * smsInstructions and smsNumber are new instructions
     * smsInstructions are optional, and will be replaced with "Text" if not provided
     */
    if (contactOptions.smsNumber !== '') {
      methods.push({
        type: ConnectNowContactMethods.SMS,
        title: 'Texting',
        is247: contactOptions.sms247,
        availableElement: (
          <ContactLink
            label={contactOptions.smsInstructions || 'Text'}
            value={contactOptions.smsNumber}
            scheme="sms"
          />
        ),
      });
    }

    /**
     * Phone
     * Always available 24/7
     */
    methods.push({
      type: ConnectNowContactMethods.Phone,
      title: 'Phone',
      is247: true, // Phone is always on
      availableElement: <ContactLink label="Call" value={contactOptions.phone} scheme="tel" />,
    });
    return methods;
  }, [contactOptions, externalNavigate, onVideoCallClick]);

  const orderedMethods = useConnectNowContactMethods({
    methods: contactMethodDefinitions,
    isDuringServiceHours,
    isDuringHoliday: isDuringHoliday(),
  });

  return (
    <>
      <Heading.h3 level={2} {...visuallyHiddenFloatStyle}>
        Ways to Connect
      </Heading.h3>

      <Layout.VStack
        {...styles.container}
        space={4}
        divider={<Divider backgroundColor="secondary.alpha.10:alpha.10" />}
      >
        {orderedMethods.map(method => {
          return (
            <Layout.View key={method.type}>
              <ContactMethod method={method} />
              {shouldShowInterpretationButton(method) && (
                <Button.tertiaryMedium
                  aria-label="Open Interpretation/ASL Services Instructions"
                  testID="button-open-interpretation-services"
                  onPress={() => setShowInterpretationModal(true)}
                >
                  Need Interpretation/ASL Services?
                </Button.tertiaryMedium>
              )}
            </Layout.View>
          );
        })}
      </Layout.VStack>

      {showBrowserWarning && (
        <BrowserIncompatibilityWarning
          crisisPhoneNumber={contactOptions.phone}
          onContinue={openBrowser}
          onClose={() => setShowBrowserWarning(false)}
        />
      )}

      {usesInterpretationServices && showInterpretationModal && (
        <InterpretationSupportModal
          // isOpen={showInterpretationModal}
          onClose={() => setShowInterpretationModal(false)}
          interpretationServicesSMS={interpretationServicesSMS}
        />
      )}
    </>
  );
};

type ContactLinkProps = {
  label: string;
  value: string;
  scheme: 'tel' | 'sms';
};

const ContactLink = ({ label, value, scheme }: ContactLinkProps): JSX.Element => (
  <Layout.VStack {...styles.contactLink} space={1}>
    <Text.paraSmall center alignItems="center">
      {label}
    </Text.paraSmall>

    <Link.paraLarge
      bold
      to={`${scheme}:${value}`}
      aria-label={`${label} ${value} to connect now (Opens in phone app)`}
      testID={`link-connect-now-${scheme}`}
    >
      {value}
    </Link.paraLarge>
  </Layout.VStack>
);

const styles = getStylesheet({
  container: {
    flex: 1,
    padding: 3,
  },

  contactLink: {
    alignItems: 'center',
  },
});
